import { setLiveEconomics } from './rubyAi/units';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Toast } from '../../components/Toast';
import {
  getAiRegistry, resolveTimeRange,
  type AiRegistryResponse, type TimeRangeValue,
} from '../../services/aiControlApi';
import AiControlHeader, { type AiControlHeaderStat } from './AiControlHeader';
import { isAiTabId, normalizeAiTab, type AiTabId } from './aiTabs';
import OverviewTab from './tabs/OverviewTab';
import ProvidersModelsTab from './tabs/ProvidersModelsTab';
import ActionsTab from './rubyAi/ActionsTab';
import VibesTab from './rubyAi/VibesTab';
import UsageMarginTab from './rubyAi/UsageMarginTab';
import RubyTokensTab from './rubyAi/RubyTokensTab';
import SettingsTab from './tabs/SettingsTab';
import LedgerTab from './ledger/LedgerTab';

export type ToastKind = 'success' | 'error' | 'info';

/** Every tab in this page receives exactly this prop object. */
export interface AiTabProps {
  registry: AiRegistryResponse | null;
  registryError: string | null;
  reloadRegistry: () => Promise<void>;
  range: { fromIso?: string; toIso?: string };
  timeRange: TimeRangeValue;
  tenantId: string | null;
  onHeaderStats: (stats: AiControlHeaderStat[]) => void;
  showToast: (message: string, type?: ToastKind) => void;
}

/** Tabs whose content is time-scoped; the picker is hidden on the others.
 *  Ledger is time-scoped too but carries its own period selector (Today default). */
const TIME_SCOPED: AiTabId[] = ['overview', 'usage', 'tokens'];

const AiControlCenter: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const urlTab = searchParams.get('tab');
  const activeTab: AiTabId = normalizeAiTab(urlTab);
  const tenantId = searchParams.get('tenantId');

  const [timeRange, setTimeRange] = useState<TimeRangeValue>({ preset: '30d' });
  const [registry, setRegistry] = useState<AiRegistryResponse | null>(null);
  const [registryError, setRegistryError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [headerStats, setHeaderStats] = useState<AiControlHeaderStat[]>([]);
  const [toast, setToast] = useState<{ message: string; type: ToastKind } | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  const showToast = useCallback((message: string, type: ToastKind = 'info') => {
    setToast({ message, type });
  }, []);

  // One registry fetch for the whole page. Routing, Providers & Models and
  // Settings all read the same rows; three fetches would let them disagree.
  const reloadRegistry = useCallback(async () => {
    setRefreshing(true);
    try {
      const reg = await getAiRegistry();
      // Live Ruby Token value + default multiplier (AI → Settings) drive every
      // token figure on this page; missing/invalid = the 1,000 / 1.5 defaults.
      setLiveEconomics(reg.config);
      setRegistry(reg);
      setRegistryError(null);
    } catch (err: any) {
      setRegistryError(err?.message || 'Failed to load the AI registry');
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { void reloadRegistry(); }, [reloadRegistry]);

  const setTab = useCallback((t: AiTabId) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', t);
    // The tenant deep link belongs to Usage; carrying it onto other tabs
    // would leave a stale filter behind the operator's back.
    if (t !== 'usage') next.delete('tenantId');
    // Per-tab selections (an action, a vibe) do not carry across tabs.
    next.delete('action');
    next.delete('vibe');
    setSearchParams(next, { replace: true });
    setHeaderStats([]);
  }, [searchParams, setSearchParams]);

  // A tab id that is not in AI_TABS (a stale bookmark, a hand-edited URL, a
  // retired tab such as ?tab=routing) is normalised into the URL — to the tab
  // that absorbed it — rather than rendering a tab at a URL that says otherwise.
  useEffect(() => {
    if (!isAiTabId(urlTab)) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', normalizeAiTab(urlTab));
      setSearchParams(next, { replace: true });
    }
  }, [urlTab, searchParams, setSearchParams]);

  const range = useMemo(() => resolveTimeRange(timeRange), [timeRange]);

  const tabProps: AiTabProps = {
    registry, registryError, reloadRegistry,
    range, timeRange, tenantId,
    onHeaderStats: setHeaderStats, showToast,
  };

  const handleRefresh = useCallback(() => {
    void reloadRegistry();
    setReloadNonce((n) => n + 1);
  }, [reloadRegistry]);

  return (
    <div className="min-h-screen bg-black -m-3 md:-m-6">
      <AiControlHeader
        stats={headerStats}
        activeTab={activeTab}
        onTabChange={setTab}
        timeRange={timeRange}
        onTimeRangeChange={setTimeRange}
        showTimeRange={TIME_SCOPED.includes(activeTab)}
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />

      {/* text-stone-900: the shell is bg-black, so anything without its own
          colour would otherwise inherit white text on these light cards. */}
      <div className="bg-stone-100 text-stone-900 min-h-[calc(100vh-80px)]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-5 space-y-5">
          {/* key={reloadNonce} makes Refresh remount the active tab, so each
              tab gets its data re-fetched without every tab exposing an
              imperative refresh handle to the shell. */}
          <div key={`${activeTab}-${reloadNonce}`} className="space-y-5">
            {activeTab === 'overview'  && <OverviewTab {...tabProps} />}
            {activeTab === 'providers' && <ProvidersModelsTab {...tabProps} />}
            {activeTab === 'actions'   && <ActionsTab {...tabProps} />}
            {activeTab === 'vibes'     && <VibesTab {...tabProps} />}
            {activeTab === 'usage'     && <UsageMarginTab {...tabProps} />}
            {activeTab === 'ledger'    && <LedgerTab {...tabProps} />}
            {activeTab === 'tokens'    && <RubyTokensTab {...tabProps} />}
            {activeTab === 'settings'  && <SettingsTab {...tabProps} />}
          </div>
        </div>
      </div>

      {/* Toast is an in-flow component; the page is tall, so it is pinned
          here rather than rendering below the fold where nobody sees it. */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        </div>
      )}
    </div>
  );
};

export default AiControlCenter;
